export function createUiShell({ rootEl, leftPanelEl, rightPanelEl, bottomPanelEl, topbarEl, hudEl, mapEl, events }) {
  const MAP_MODE_KEY = "hydrotools:map-mode";
  const UI_THEME_KEY = "hydrotools:ui-theme";
  const LEFT_W_KEY = "hydrotools:leftPanelW";
  const RIGHT_W_KEY = "hydrotools:rightPanelW";
  const BOTTOM_H_KEY = "hydrotools:bottomPanelH";
  const LEFT_OPEN_KEY = "hydrotools:leftOpen";
  const RIGHT_OPEN_KEY = "hydrotools:rightOpen";
  const BOTTOM_OPEN_KEY = "hydrotools:bottomOpen";
  const PROJECT_SCHEMA = "hydrotools.project";
  const PROJECT_VERSION = 1;
  const PROJECT_DATA_POLICY_KEY = "hydrotools:project-data-policy";
  const PROJECT_EMBED_MAX_SOUNDINGS_KEY = "hydrotools:project-embed-max-soundings";
  const PROJECT_AUTOSAVE_ENABLED_KEY = "hydrotools:project-autosave-enabled";
  const PROJECT_AUTOSAVE_INTERVAL_KEY = "hydrotools:project-autosave-interval-sec";
  const PROJECT_AUTOSAVE_SNAPSHOT_KEY = "hydrotools:project-autosave-snapshot:v1";
  const PROJECT_AUTOSAVE_AT_KEY = "hydrotools:project-autosave-at:v1";
  const FULLSCREEN_SIM_HANDOFF_KEY = "hydrotools:fullscreen-handoff:simulator:v1";

  let themeToggleBtn = null;
  let captureBtn = null;
  let captureClipboardBtn = null;
  let settingsModalEl = null;
  let settingsPanelEl = null;
  let settingsOpen = false;
  let runtimeRef = null;
  let lastRestoreReport = null;
  let relinkItems = [];
  let pendingRelinkRef = null;
  let autosaveTimer = null;
  let fullscreenBtnEl = null;
  let fullscreenExitBtnEl = null;
  let fullscreenBound = false;
  let fileDropOverlayEl = null;
  let fileDropDepth = 0;
  const bottomPanelEntries = new Map();
  let activeBottomPanelId = "";

  const UI_THEMES = [
    { id: "deep-blue", name: "Deep Blue" },
    { id: "steel-harbor", name: "Steel Harbor" },
    { id: "survey-slate", name: "Survey Slate" },
    { id: "red-watch", name: "Red Watch" },
    { id: "matrix-green", name: "Matrix Green" },
    { id: "rose-mist", name: "Rose Mist" },
  ];

  function dispatchMapResizePulse() {
    setTimeout(() => window.dispatchEvent(new Event("resize")), 0);
    setTimeout(() => window.dispatchEvent(new Event("resize")), 180);
    setTimeout(() => window.dispatchEvent(new Event("resize")), 360);
  }

  function applyMapMode(mode, emit = true) {
    const nextMode = mode === "dark" ? "dark" : "light";
    localStorage.setItem(MAP_MODE_KEY, nextMode);
    if (mapEl) mapEl.dataset.mapMode = nextMode;
    if (themeToggleBtn) {
      const target = nextMode === "dark" ? "light" : "dark";
      themeToggleBtn.dataset.mode = nextMode;
      themeToggleBtn.title = `Switch to ${target} map`;
      themeToggleBtn.setAttribute("aria-label", themeToggleBtn.title);
    }
    if (emit) events.emit("map:basemap:mode", { mode: nextMode });
  }

  function applyUiTheme(themeId) {
    const valid = UI_THEMES.some((t) => t.id === themeId);
    const next = valid ? themeId : "deep-blue";
    rootEl.dataset.uiTheme = next;
    localStorage.setItem(UI_THEME_KEY, next);
    const sel = settingsModalEl?.querySelector?.("#settings-ui-theme");
    if (sel) sel.value = next;
  }

  function ensureThemeToggle() {
    if (!mapEl?.parentElement || themeToggleBtn) return;
    themeToggleBtn = document.createElement("button");
    themeToggleBtn.type = "button";
    themeToggleBtn.className = "map-theme-toggle";
    themeToggleBtn.id = "map-theme-toggle";
    themeToggleBtn.innerHTML = `
      <span class="map-theme-icon sun" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
          <path d="M12 4a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0V5a1 1 0 0 1 1-1Zm0 13a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm8-6a1 1 0 0 1-1 1h-1a1 1 0 1 1 0-2h1a1 1 0 0 1 1 1ZM7 12a1 1 0 0 1-1 1H5a1 1 0 1 1 0-2h1a1 1 0 0 1 1 1Zm9.66 5.24a1 1 0 0 1 1.41 0l.7.7a1 1 0 1 1-1.41 1.42l-.7-.71a1 1 0 0 1 0-1.41Zm-10.61 0a1 1 0 0 1 0 1.41l-.7.71a1 1 0 1 1-1.42-1.42l.71-.7a1 1 0 0 1 1.41 0Zm12.72-11.9a1 1 0 0 1 0 1.42l-.7.7a1 1 0 0 1-1.41-1.41l.7-.71a1 1 0 0 1 1.41 0ZM5.35 5.34a1 1 0 0 1 1.41 0l.7.71a1 1 0 0 1-1.41 1.41l-.71-.7a1 1 0 0 1 0-1.42Z"/>
        </svg>
      </span>
      <span class="map-theme-icon moon" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
          <path d="M14.94 3.5a1 1 0 0 1 .54 1.87A7.5 7.5 0 1 0 18.63 15a1 1 0 1 1 1.4 1.44A9.5 9.5 0 1 1 13.5 3.02a1 1 0 0 1 1.44.48Z"/>
        </svg>
      </span>
    `;
    themeToggleBtn.addEventListener("click", () => {
      const current = themeToggleBtn?.dataset?.mode === "dark" ? "dark" : "light";
      applyMapMode(current === "dark" ? "light" : "dark");
    });
    mapEl.parentElement.appendChild(themeToggleBtn);
    const saved = localStorage.getItem(MAP_MODE_KEY);
    applyMapMode(saved === "dark" ? "dark" : "light", false);
  }

  function formatCaptureTimestamp() {
    const now = new Date();
    const pad = (value) => String(value).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
  }

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function escapeUiText(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }

  function renderBottomPanelHost() {
    const entries = [...bottomPanelEntries.values()];
    if (!entries.length) {
      bottomPanelEl.innerHTML = `<div class="panel-content"><div class="small">Bottom panel ready.</div></div>`;
      addResizer(bottomPanelEl, "top", "--bottomPanelH", BOTTOM_H_KEY);
      return;
    }
    if (!entries.some((entry) => entry.id === activeBottomPanelId)) {
      activeBottomPanelId = entries[entries.length - 1]?.id || "";
    }
    const activeEntry = entries.find((entry) => entry.id === activeBottomPanelId) || entries[entries.length - 1];
    const tabsHtml = entries.length > 1
      ? `
        <div class="bottom-panel-tabs" role="tablist" aria-label="Bottom panel toolsets">
          ${entries.map((entry) => `
            <button
              type="button"
              class="bottom-panel-tab ${entry.id === activeEntry.id ? "is-active" : ""}"
              data-bottom-panel-tab="${escapeUiText(entry.id)}"
              role="tab"
              aria-selected="${entry.id === activeEntry.id ? "true" : "false"}"
            >${escapeUiText(entry.title || entry.id)}</button>
          `).join("")}
        </div>
      `
      : "";
    bottomPanelEl.innerHTML = `
      <div class="panel-content bottom-panel-stack">
        ${tabsHtml}
        <div class="bottom-panel-body"></div>
      </div>
    `;
    const bodyHost = bottomPanelEl.querySelector(".bottom-panel-body");
    if (bodyHost && activeEntry?.bodyEl) bodyHost.appendChild(activeEntry.bodyEl);
    bottomPanelEl.querySelectorAll("[data-bottom-panel-tab]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const nextId = String(btn.getAttribute("data-bottom-panel-tab") || "");
        if (!nextId || nextId === activeBottomPanelId) return;
        activeBottomPanelId = nextId;
        renderBottomPanelHost();
      });
    });
    try {
      activeEntry?.onShow?.();
    } catch (err) {
      console.error("[UI] bottom panel onShow failed", err);
    }
    addResizer(bottomPanelEl, "top", "--bottomPanelH", BOTTOM_H_KEY);
  }

  function canvasToBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Unable to create image export."));
      }, "image/png");
    });
  }

  function setCaptureButtonsBusy(isBusy) {
    [captureBtn, captureClipboardBtn].forEach((btn) => {
      if (!btn) return;
      btn.dataset.busy = isBusy ? "true" : "false";
      btn.disabled = !!isBusy;
    });
  }

  async function renderMapCaptureBlob() {
    const captureHost = mapEl?.parentElement;
    if (!mapEl || !captureHost) {
      throw new Error("Map capture is unavailable");
    }
    const mapInstance = runtimeRef?.map?.instance;
    mapInstance?.invalidateSize?.(false);
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const html2canvas = (await import("html2canvas")).default;
    const canvas = await html2canvas(captureHost, {
      backgroundColor: null,
      useCORS: true,
      logging: false,
      scale: Math.max(1, Math.min(4, window.devicePixelRatio || 2)),
      ignoreElements(element) {
        if (!(element instanceof Element)) return false;
        if (element.closest("#hud")) return true;
        if (element.closest("#map-theme-toggle")) return true;
        if (element.closest("#map-capture-toggle")) return true;
        if (element.closest("#map-capture-clipboard-toggle")) return true;
        if (element.closest(".map-measure-control")) return true;
        if (element.closest(".map-search-control")) return true;
        if (element.closest(".map-rotate-control")) return true;
        if (element.closest(".leaflet-control-zoom")) return true;
        return false;
      },
    });
    return canvasToBlob(canvas);
  }

  async function captureMapViewport() {
    if (captureBtn?.dataset?.busy === "true" || captureClipboardBtn?.dataset?.busy === "true") return;
    try {
      setCaptureButtonsBusy(true);
      const blob = await renderMapCaptureBlob();
      downloadBlob(blob, `hydrotools-map-capture_${formatCaptureTimestamp()}.png`);
      toast("Map capture saved");
    } catch (err) {
      console.error("[UI] map capture failed", err);
      toast(err?.message || "Map capture failed");
    } finally {
      setCaptureButtonsBusy(false);
    }
  }

  async function copyMapViewportToClipboard() {
    if (captureBtn?.dataset?.busy === "true" || captureClipboardBtn?.dataset?.busy === "true") return;
    try {
      if (!window.isSecureContext) {
        throw new Error("Clipboard image copy requires HTTPS or localhost");
      }
      if (typeof ClipboardItem === "undefined" || !navigator?.clipboard?.write) {
        throw new Error("Image clipboard copy is not supported in this browser");
      }
      setCaptureButtonsBusy(true);
      const blob = await renderMapCaptureBlob();
      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type || "image/png"]: blob,
        }),
      ]);
      toast("Map capture copied to clipboard");
    } catch (err) {
      console.error("[UI] map capture clipboard copy failed", err);
      const msg = String(err?.message || "Clipboard copy failed");
      if (msg.includes("HTTPS or localhost")) {
        toast("Clipboard copy needs HTTPS or localhost. Saved PNG instead.");
        try {
          const blob = await renderMapCaptureBlob();
          downloadBlob(blob, `hydrotools-map-capture_${formatCaptureTimestamp()}.png`);
        } catch {}
      } else {
        toast(msg);
      }
    } finally {
      setCaptureButtonsBusy(false);
    }
  }

  function ensureCaptureButton() {
    if (!mapEl?.parentElement || captureBtn || captureClipboardBtn) return;
    captureBtn = document.createElement("button");
    captureBtn.type = "button";
    captureBtn.className = "map-measure-btn map-capture-btn";
    captureBtn.id = "map-capture-toggle";
    captureBtn.title = "Save map capture";
    captureBtn.setAttribute("aria-label", captureBtn.title);
    captureBtn.dataset.busy = "false";
    captureBtn.innerHTML = `
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <path d="M7.5 7.5 9 5h6l1.5 2.5H19A2.5 2.5 0 0 1 21.5 10v7A2.5 2.5 0 0 1 19 19.5H5A2.5 2.5 0 0 1 2.5 17v-7A2.5 2.5 0 0 1 5 7.5h2.5Zm4.5 8.2a3.7 3.7 0 1 0 0-7.4 3.7 3.7 0 0 0 0 7.4Zm0-1.8a1.9 1.9 0 1 1 0-3.8 1.9 1.9 0 0 1 0 3.8Z"/>
      </svg>
    `;
    captureBtn.addEventListener("click", () => {
      captureMapViewport();
    });
    mapEl.parentElement.appendChild(captureBtn);

    captureClipboardBtn = document.createElement("button");
    captureClipboardBtn.type = "button";
    captureClipboardBtn.className = "map-measure-btn map-capture-btn map-capture-clipboard-btn";
    captureClipboardBtn.id = "map-capture-clipboard-toggle";
    captureClipboardBtn.title = "Copy map capture to clipboard";
    captureClipboardBtn.setAttribute("aria-label", captureClipboardBtn.title);
    captureClipboardBtn.dataset.busy = "false";
    captureClipboardBtn.innerHTML = `
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <path d="M9 3.5A2.5 2.5 0 0 0 6.5 6v11A2.5 2.5 0 0 0 9 19.5h8A2.5 2.5 0 0 0 19.5 17V6A2.5 2.5 0 0 0 17 3.5h-1.26A2.75 2.75 0 0 0 13.1 2h-.2a2.75 2.75 0 0 0-2.64 1.5H9Zm2.1.3c.13 0 .24-.08.29-.2.2-.47.66-.8 1.21-.8h.2c.55 0 1.01.33 1.21.8.05.12.16.2.29.2H17A1.7 1.7 0 0 1 18.7 5.5v12A1.7 1.7 0 0 1 17 19.2H9A1.7 1.7 0 0 1 7.3 17.5v-12A1.7 1.7 0 0 1 9 3.8h2.1Zm-4.8 3.4a.75.75 0 0 1 .75.75v10.55c0 .41.34.75.75.75h7.95a.75.75 0 1 1 0 1.5H7.8a2.25 2.25 0 0 1-2.25-2.25V7.95a.75.75 0 0 1 .75-.75Z"/>
      </svg>
    `;
    captureClipboardBtn.addEventListener("click", () => {
      copyMapViewportToClipboard();
    });
    mapEl.parentElement.appendChild(captureClipboardBtn);
  }

  function addResizer(panelEl, side, cssVarName, storageKey) {
    if (!panelEl || panelEl.querySelector(`.panel-resizer.${side}`)) return;
    const grip = document.createElement("div");
    grip.className = `panel-resizer ${side}`;
    grip.style.zIndex = "3000";
    grip.style.touchAction = "none";
    panelEl.appendChild(grip);
    const saved = localStorage.getItem(storageKey);
    if (saved) document.documentElement.style.setProperty(cssVarName, saved);

    let dragging = false;
    let maybeClick = false;
    let moved = false;
    let startX = 0;
    let startY = 0;

    const onMove = (e) => {
      if (!dragging) return;
      if (maybeClick) {
        const dx = Math.abs(e.clientX - startX);
        const dy = Math.abs(e.clientY - startY);
        if (dx > 4 || dy > 4) {
          moved = true;
          maybeClick = false;
        }
      }
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (side === "left") {
        const wPx = Math.max(260, Math.min(e.clientX, Math.round(vw * 0.45)));
        const w = `${wPx}px`;
        document.documentElement.style.setProperty(cssVarName, w);
        localStorage.setItem(storageKey, w);
      } else if (side === "right") {
        const wPx = Math.max(280, Math.min(vw - e.clientX, Math.round(vw * 0.55)));
        const w = `${wPx}px`;
        document.documentElement.style.setProperty(cssVarName, w);
        localStorage.setItem(storageKey, w);
      } else if (side === "top") {
        const hPx = Math.max(140, Math.min(vh - e.clientY, Math.round(vh * 0.55)));
        const h = `${hPx}px`;
        document.documentElement.style.setProperty(cssVarName, h);
        localStorage.setItem(storageKey, h);
      }
    };

    const stop = () => {
      if (!dragging) return;
      dragging = false;
      document.body.classList.remove("resizing");
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      if (!moved) {
        if (side === "left") setDockOpen("left", !isDockOpen("left"));
        else if (side === "right") setDockOpen("right", !isDockOpen("right"));
        else if (side === "top") setDockOpen("bottom", !isDockOpen("bottom"));
      }
      maybeClick = false;
      moved = false;
      dispatchMapResizePulse();
    };

    grip.addEventListener("pointerdown", (e) => {
      dragging = true;
      maybeClick = true;
      moved = false;
      startX = e.clientX;
      startY = e.clientY;
      document.body.classList.add("resizing");
      grip.setPointerCapture?.(e.pointerId);
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", stop);
      window.addEventListener("pointercancel", stop);
    });
  }

  function setDockOpen(which, open) {
    if (which === "left") rootEl.classList.toggle("left-collapsed", !open);
    if (which === "right") rootEl.classList.toggle("right-collapsed", !open);
    if (which === "bottom") rootEl.classList.toggle("bottom-collapsed", !open);
    localStorage.setItem(which === "left" ? LEFT_OPEN_KEY : which === "right" ? RIGHT_OPEN_KEY : BOTTOM_OPEN_KEY, open ? "1" : "0");
    dispatchMapResizePulse();
  }

  function isDockOpen(which) {
    return !rootEl.classList.contains(`${which}-collapsed`);
  }

  function ensureDockToggles() {
    if (!rootEl) return;
    const leftOpen = localStorage.getItem(LEFT_OPEN_KEY);
    const rightOpen = localStorage.getItem(RIGHT_OPEN_KEY);
    const bottomOpen = localStorage.getItem(BOTTOM_OPEN_KEY);
    setDockOpen("left", leftOpen !== "0");
    setDockOpen("right", rightOpen !== "0");
    setDockOpen("bottom", bottomOpen !== "0");
  }

  function closeSettingsModal() {
    if (!settingsModalEl || !settingsPanelEl || !settingsOpen) return;
    settingsOpen = false;
    settingsModalEl.setAttribute("aria-hidden", "true");
    settingsPanelEl.setAttribute("aria-hidden", "true");
    document.body.classList.remove("settings-open");
  }

  function openSettingsModal() {
    if (!settingsModalEl || !settingsPanelEl || settingsOpen) return;
    settingsOpen = true;
    settingsModalEl.setAttribute("aria-hidden", "false");
    settingsPanelEl.setAttribute("aria-hidden", "false");
    document.body.classList.add("settings-open");
    renderRestoreReport(lastRestoreReport);
    updateProjectEstimate();
    const themeSel = settingsModalEl.querySelector("#settings-ui-theme");
    if (themeSel) themeSel.value = rootEl?.dataset?.uiTheme || localStorage.getItem(UI_THEME_KEY) || "deep-blue";
    const stampEl = settingsModalEl.querySelector("#settings-project-autosave-stamp");
    if (stampEl) stampEl.textContent = localStorage.getItem(PROJECT_AUTOSAVE_AT_KEY) || "never";
  }

  function normalizeRestoreReport(report) {
    const base = report && typeof report === "object" ? report : {};
    const unresolved = Array.isArray(base.unresolved) ? base.unresolved : [];
    const errors = Array.isArray(base.errors) ? base.errors : [];
    return {
      restored: Number(base.restored || 0),
      unresolved,
      errors,
      byTool: base.byTool && typeof base.byTool === "object" ? base.byTool : {},
    };
  }

  function renderRestoreReport(report) {
    const host = settingsModalEl?.querySelector?.("#settings-restore-report");
    if (!host) return;
    if (!report) {
      host.innerHTML = `<div class="small">No project load report yet.</div>`;
      relinkItems = [];
      return;
    }
    const r = normalizeRestoreReport(report);
    relinkItems = r.unresolved.slice();
    const unresolvedRows = relinkItems.length
      ? relinkItems
          .map((u, idx) => `
            <div class="row" style="justify-content:space-between; gap:8px; margin-top:6px; padding:6px 8px; border:1px solid rgba(148,163,184,.22); border-radius:8px;">
              <div style="min-width:0;">
                <div style="font-size:12px; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${u.label || u.referenceId || "Unresolved source"}</div>
                <div class="small">${u.hint || "Source missing, relink required."}${u.expectedFileName ? ` Expected: ${u.expectedFileName}` : ""}</div>
              </div>
              <button type="button" class="btn" data-project-relink="${idx}">Relink</button>
            </div>
          `)
          .join("")
      : `<div class="small">No unresolved sources.</div>`;
    const errorRows = r.errors.length
      ? `<div class="small" style="margin-top:6px;">Errors: ${r.errors.map((e) => e.message || e).slice(0, 3).join(" | ")}</div>`
      : "";
    host.innerHTML = `
      <div class="small">Restored <strong>${r.restored.toLocaleString()}</strong> item(s), unresolved <strong>${r.unresolved.length.toLocaleString()}</strong>.</div>
      ${unresolvedRows}
      ${errorRows}
    `;
  }

  function getPanelSize(varName, fallback = "") {
    const computed = getComputedStyle(document.documentElement).getPropertyValue(varName)?.trim();
    if (computed) return computed;
    return localStorage.getItem(fallback) || "";
  }

  function getProjectSaveOptions() {
    const dataPolicy = String(settingsModalEl?.querySelector?.("#settings-project-data-policy")?.value || localStorage.getItem(PROJECT_DATA_POLICY_KEY) || "reference");
    const embedMaxSoundingPoints = Math.max(0, Number(settingsModalEl?.querySelector?.("#settings-project-embed-max")?.value || localStorage.getItem(PROJECT_EMBED_MAX_SOUNDINGS_KEY) || 0) || 0);
    localStorage.setItem(PROJECT_DATA_POLICY_KEY, dataPolicy);
    localStorage.setItem(PROJECT_EMBED_MAX_SOUNDINGS_KEY, String(embedMaxSoundingPoints));
    return { dataPolicy, embedMaxSoundingPoints };
  }

  function buildProjectPayload(options = null) {
    const saveOptions = options || getProjectSaveOptions();
    const dataPolicy = String(saveOptions.dataPolicy || "reference");
    const mapInstance = runtimeRef?.map?.instance;
    const center = mapInstance?.getCenter?.();
    const view = center ? { center: [center.lat, center.lng], zoom: mapInstance.getZoom?.() } : null;
    return {
      schema: PROJECT_SCHEMA,
      version: PROJECT_VERSION,
      savedAt: new Date().toISOString(),
      project: {
        dataPolicy,
        embedMaxSoundingPoints: Number(saveOptions.embedMaxSoundingPoints || 0),
      },
      ui: {
        theme: rootEl?.dataset?.uiTheme || localStorage.getItem(UI_THEME_KEY) || "deep-blue",
        mapMode: mapEl?.dataset?.mapMode || localStorage.getItem(MAP_MODE_KEY) || "light",
        docks: {
          leftOpen: isDockOpen("left"),
          rightOpen: isDockOpen("right"),
          bottomOpen: isDockOpen("bottom"),
        },
        sizes: {
          leftPanelW: getPanelSize("--leftPanelW", LEFT_W_KEY),
          rightPanelW: getPanelSize("--rightPanelW", RIGHT_W_KEY),
          bottomPanelH: getPanelSize("--bottomPanelH", BOTTOM_H_KEY),
        },
      },
      map: view ? { view } : {},
      tools: runtimeRef?.tools?.exportProjectData?.({
        dataPolicy,
        embedMaxSoundingPoints: Number(saveOptions.embedMaxSoundingPoints || 0),
      }) || {},
    };
  }

  function estimateProjectSizeBytes(payload) {
    const json = JSON.stringify(payload);
    return new Blob([json]).size;
  }

  function formatBytes(bytes) {
    const n = Number(bytes) || 0;
    if (n < 1024) return `${n} B`;
    const kb = n / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(2)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  }

  function updateProjectEstimate() {
    const label = settingsModalEl?.querySelector?.("#settings-project-estimate");
    if (!label) return;
    try {
      const payload = buildProjectPayload();
      const size = estimateProjectSizeBytes(payload);
      label.textContent = `Estimated size: ${formatBytes(size)}`;
      label.style.color = size > (25 * 1024 * 1024) ? "#fca5a5" : "";
    } catch {
      label.textContent = "Estimated size: n/a";
    }
  }

  async function saveProjectFile() {
    try {
      const payload = buildProjectPayload();
      const size = estimateProjectSizeBytes(payload);
      if (size > (25 * 1024 * 1024) && payload?.project?.dataPolicy === "embed") {
        const ok = window.confirm(`Project is large (${formatBytes(size)}). Continue saving?`);
        if (!ok) return;
      }
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `hydrotools-project-${stamp}.hydroproj.json`;
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast("Project saved");
    } catch (error) {
      console.error("[Settings] project save failed", error);
      toast("Project save failed");
    }
  }

  async function saveProjectBundleFile() {
    try {
      const payload = buildProjectPayload();
      const size = estimateProjectSizeBytes(payload);
      if (size > (25 * 1024 * 1024) && payload?.project?.dataPolicy === "embed") {
        const ok = window.confirm(`Bundle is large (${formatBytes(size)}). Continue saving?`);
        if (!ok) return;
      }
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      zip.file("project.hydroproj.json", JSON.stringify(payload, null, 2));
      const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `hydrotools-project-${stamp}.hydroproj.zip`;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast("Project bundle saved");
    } catch (error) {
      console.error("[Settings] project bundle save failed", error);
      toast("Bundle save failed");
    }
  }

  function migrateProjectData(input) {
    if (!input || typeof input !== "object") throw new Error("Invalid project file structure.");
    const out = { ...input };
    const hasSchema = typeof out.schema === "string" && out.schema.trim().length > 0;
    const hasVersion = Number.isFinite(Number(out.version));
    if (!hasSchema && !hasVersion) {
      // Legacy scaffold fallback.
      return {
        ...out,
        schema: PROJECT_SCHEMA,
        version: PROJECT_VERSION,
      };
    }
    if (hasSchema && out.schema !== PROJECT_SCHEMA) {
      throw new Error(`Unsupported project schema "${out.schema}".`);
    }
    const rawVersion = Number(out.version);
    if (!Number.isInteger(rawVersion) || rawVersion <= 0) {
      throw new Error("Project version is invalid.");
    }
    if (rawVersion > PROJECT_VERSION) {
      throw new Error(`Project version ${rawVersion} is newer than this app (${PROJECT_VERSION}).`);
    }
    // Placeholder migration chain for future format changes.
    let current = { ...out, schema: PROJECT_SCHEMA, version: rawVersion };
    while (current.version < PROJECT_VERSION) {
      switch (current.version) {
        default:
          throw new Error(`No migration path from version ${current.version}.`);
      }
    }
    return current;
  }

  function validateProjectData(data) {
    if (!data || typeof data !== "object") throw new Error("Project payload must be an object.");
    if (data.schema !== PROJECT_SCHEMA) throw new Error(`Project schema must be "${PROJECT_SCHEMA}".`);
    if (!Number.isInteger(data.version) || data.version !== PROJECT_VERSION) {
      throw new Error(`Project version must be ${PROJECT_VERSION}.`);
    }
    if (data.ui && typeof data.ui !== "object") throw new Error("Project ui section is invalid.");
    if (data.map && typeof data.map !== "object") throw new Error("Project map section is invalid.");
    if (data.tools && typeof data.tools !== "object") throw new Error("Project tools section is invalid.");
    if (data.project && typeof data.project !== "object") throw new Error("Project project section is invalid.");
  }

  function applyProjectData(data) {
    const normalized = migrateProjectData(data);
    validateProjectData(normalized);

    const ui = normalized.ui && typeof normalized.ui === "object" ? normalized.ui : {};
    const docks = ui.docks && typeof ui.docks === "object" ? ui.docks : {};
    const sizes = ui.sizes && typeof ui.sizes === "object" ? ui.sizes : {};
    const dataPolicy = String(normalized?.project?.dataPolicy || localStorage.getItem(PROJECT_DATA_POLICY_KEY) || "reference");
    localStorage.setItem(PROJECT_DATA_POLICY_KEY, dataPolicy);
    const policySel = settingsModalEl?.querySelector?.("#settings-project-data-policy");
    if (policySel) policySel.value = dataPolicy;

    if (ui.theme) applyUiTheme(ui.theme);
    if (ui.mapMode) applyMapMode(ui.mapMode);

    if (sizes.leftPanelW) {
      document.documentElement.style.setProperty("--leftPanelW", String(sizes.leftPanelW));
      localStorage.setItem(LEFT_W_KEY, String(sizes.leftPanelW));
    }
    if (sizes.rightPanelW) {
      document.documentElement.style.setProperty("--rightPanelW", String(sizes.rightPanelW));
      localStorage.setItem(RIGHT_W_KEY, String(sizes.rightPanelW));
    }
    if (sizes.bottomPanelH) {
      document.documentElement.style.setProperty("--bottomPanelH", String(sizes.bottomPanelH));
      localStorage.setItem(BOTTOM_H_KEY, String(sizes.bottomPanelH));
    }

    if (typeof docks.leftOpen === "boolean") setDockOpen("left", docks.leftOpen);
    if (typeof docks.rightOpen === "boolean") setDockOpen("right", docks.rightOpen);
    if (typeof docks.bottomOpen === "boolean") setDockOpen("bottom", docks.bottomOpen);

    const view = normalized.map?.view;
    if (view && Array.isArray(view.center) && view.center.length >= 2 && Number.isFinite(view.zoom)) {
      runtimeRef?.map?.instance?.setView?.([view.center[0], view.center[1]], view.zoom, { animate: false });
    }

    let restoreReport = null;
    if (normalized.tools && runtimeRef?.tools?.importProjectData) {
      restoreReport = runtimeRef.tools.importProjectData(normalized.tools, { dataPolicy });
    }
    lastRestoreReport = normalizeRestoreReport(restoreReport);
    renderRestoreReport(lastRestoreReport);
    events.emit("project:restore:report", lastRestoreReport);

    dispatchMapResizePulse();
  }

  async function readProjectFromZipFile(file) {
    const { default: JSZip } = await import("jszip");
    const zip = await JSZip.loadAsync(file);
    const jsonEntry = Object.values(zip.files).find((f) => !f.dir && /\.hydroproj\.json$/i.test(f.name))
      || Object.values(zip.files).find((f) => !f.dir && /\.json$/i.test(f.name));
    if (!jsonEntry) throw new Error("No project JSON found in zip.");
    const raw = await jsonEntry.async("string");
    return JSON.parse(raw);
  }

  function loadProjectFile(file) {
    if (!file) return;
    const isZip = /\.zip$/i.test(file.name || "");
    if (isZip) {
      readProjectFromZipFile(file)
        .then((parsed) => {
          applyProjectData(parsed);
          toast("Project bundle loaded");
        })
        .catch((error) => {
          console.error("[Settings] project bundle load failed", error);
          const msg = String(error?.message || "Project bundle load failed");
          toast(msg.length > 140 ? `${msg.slice(0, 137)}...` : msg);
        });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const raw = String(reader.result || "");
        const parsed = JSON.parse(raw);
        applyProjectData(parsed);
        toast("Project loaded");
      } catch (error) {
        console.error("[Settings] project load failed", error);
        const msg = String(error?.message || "Project load failed");
        toast(msg.length > 140 ? `${msg.slice(0, 137)}...` : msg);
      }
    };
    reader.onerror = () => {
      toast("Project load failed");
    };
    reader.readAsText(file);
  }

  function saveAutosaveSnapshot() {
    try {
      const payload = buildProjectPayload();
      localStorage.setItem(PROJECT_AUTOSAVE_SNAPSHOT_KEY, JSON.stringify(payload));
      localStorage.setItem(PROJECT_AUTOSAVE_AT_KEY, new Date().toISOString());
      const stampEl = settingsModalEl?.querySelector?.("#settings-project-autosave-stamp");
      if (stampEl) stampEl.textContent = localStorage.getItem(PROJECT_AUTOSAVE_AT_KEY) || "never";
    } catch (error) {
      console.warn("[Settings] autosave failed", error);
    }
  }

  function restoreAutosaveSnapshot() {
    try {
      const raw = localStorage.getItem(PROJECT_AUTOSAVE_SNAPSHOT_KEY);
      if (!raw) {
        toast("No autosave snapshot found");
        return;
      }
      const parsed = JSON.parse(raw);
      applyProjectData(parsed);
      toast("Autosave restored");
    } catch (error) {
      console.error("[Settings] autosave restore failed", error);
      toast("Autosave restore failed");
    }
  }

  function clearAutosaveSnapshot() {
    localStorage.removeItem(PROJECT_AUTOSAVE_SNAPSHOT_KEY);
    localStorage.removeItem(PROJECT_AUTOSAVE_AT_KEY);
    const stampEl = settingsModalEl?.querySelector?.("#settings-project-autosave-stamp");
    if (stampEl) stampEl.textContent = "never";
    toast("Autosave snapshot cleared");
  }

  function configureAutosave() {
    if (autosaveTimer) {
      clearInterval(autosaveTimer);
      autosaveTimer = null;
    }
    const enabled = localStorage.getItem(PROJECT_AUTOSAVE_ENABLED_KEY) === "1";
    const sec = Math.max(15, Number(localStorage.getItem(PROJECT_AUTOSAVE_INTERVAL_KEY) || 90) || 90);
    if (!enabled) return;
    autosaveTimer = setInterval(() => {
      saveAutosaveSnapshot();
    }, sec * 1000);
  }

  function ensureSettingsModal() {
    if (settingsModalEl && settingsPanelEl) return;
    settingsModalEl = document.createElement("div");
    settingsModalEl.className = "settings-backdrop";
    settingsModalEl.setAttribute("aria-hidden", "true");
    settingsModalEl.innerHTML = `
      <div class="settings-modal" role="dialog" aria-modal="true" aria-labelledby="settings-title" aria-hidden="true">
        <div class="settings-head">
          <div>
            <div id="settings-title" class="settings-title">Global Settings</div>
            <div class="small">HydroTools workspace-level preferences</div>
          </div>
          <button type="button" class="iconbtn" id="settings-close" title="Close">x</button>
        </div>
        <div class="settings-body">
          <div class="settings-section">
            <div class="settings-section-title">Appearance</div>
            <label class="small" for="settings-ui-theme">Theme</label>
            <select id="settings-ui-theme" class="settings-theme-select" style="margin-top:6px; min-width:220px;">
              ${UI_THEMES.map((t) => `<option value="${t.id}">${t.name}</option>`).join("")}
            </select>
            <div class="small" style="margin-top:6px;">Theme changes apply globally to panels, menus, and controls.</div>
          </div>
          <div class="settings-section">
            <div class="settings-section-title">Project</div>
            <div class="row" style="gap:8px; align-items:center; margin-bottom:8px;">
              <label class="small" for="settings-project-data-policy" style="min-width:84px;">Data policy</label>
              <select id="settings-project-data-policy" style="min-width:170px;">
                <option value="reference">Reference (lightweight)</option>
                <option value="embed">Embed data (portable)</option>
              </select>
              <label class="small" for="settings-project-embed-max" style="min-width:72px;">Embed max pts/layer</label>
              <input id="settings-project-embed-max" type="number" min="0" step="1000" style="width:110px;" />
            </div>
            <div class="row" style="gap:8px;">
              <button type="button" class="btn" id="settings-project-save">Save Project</button>
              <button type="button" class="btn" id="settings-project-save-bundle">Save Bundle (.zip)</button>
              <button type="button" class="btn" id="settings-project-load">Load Project</button>
              <button type="button" class="btn" id="settings-project-load-bundle">Load Bundle (.zip)</button>
              <button type="button" class="btn" id="settings-project-estimate-btn">Estimate Size</button>
              <input id="settings-project-file" type="file" accept=".json,.hydroproj,.hydroproj.json,application/json,.zip" style="display:none" />
              <input id="settings-project-relink-file" type="file" style="display:none" />
              <input id="settings-project-relink-folder" type="file" webkitdirectory directory multiple style="display:none" />
            </div>
            <div class="small" style="margin-top:6px;">Saves map/layout/tool state. Use Reference for small files, Embed for portable datasets.</div>
            <div id="settings-project-estimate" class="small" style="margin-top:4px;">Estimated size: n/a</div>
            <div id="settings-restore-report" style="margin-top:8px;"></div>
            <div class="row" style="gap:8px; margin-top:8px;">
              <button type="button" class="btn" id="settings-project-batch-relink">Batch Relink (Folder)</button>
            </div>
          </div>
          <div class="settings-section">
            <div class="settings-section-title">Performance</div>
            <div class="row" style="gap:8px; align-items:center; margin-bottom:6px;">
              <label class="small"><input id="settings-project-autosave-enabled" type="checkbox" /> Autosave project snapshot</label>
              <label class="small">Interval (sec)</label>
              <input id="settings-project-autosave-interval" type="number" min="15" step="15" style="width:80px;" />
              <button type="button" class="btn" id="settings-project-restore-autosave">Restore Last Autosave</button>
              <button type="button" class="btn" id="settings-project-clear-autosave">Clear Autosave</button>
            </div>
            <div class="small">Last autosave: <span id="settings-project-autosave-stamp">never</span></div>
          </div>
        </div>
      </div>
    `;
    rootEl.appendChild(settingsModalEl);
    settingsPanelEl = settingsModalEl.querySelector(".settings-modal");

    settingsModalEl.addEventListener("click", (e) => {
      if (e.target === settingsModalEl) {
        closeSettingsModal();
      }
    });
    settingsModalEl.querySelector("#settings-ui-theme")?.addEventListener("change", (e) => {
      applyUiTheme(String(e?.target?.value || "deep-blue"));
    });

    settingsModalEl.querySelector("#settings-close")?.addEventListener("click", closeSettingsModal);
    settingsModalEl.querySelector("#settings-project-save")?.addEventListener("click", saveProjectFile);
    settingsModalEl.querySelector("#settings-project-save-bundle")?.addEventListener("click", saveProjectBundleFile);
    settingsModalEl.querySelector("#settings-project-load")?.addEventListener("click", () => {
      settingsModalEl.querySelector("#settings-project-file")?.click();
    });
    settingsModalEl.querySelector("#settings-project-load-bundle")?.addEventListener("click", () => {
      settingsModalEl.querySelector("#settings-project-file")?.click();
    });
    settingsModalEl.querySelector("#settings-project-estimate-btn")?.addEventListener("click", updateProjectEstimate);
    settingsModalEl.querySelector("#settings-project-file")?.addEventListener("change", (e) => {
      const file = e.target?.files?.[0];
      if (file) loadProjectFile(file);
      e.target.value = "";
    });
    settingsModalEl.addEventListener("click", async (e) => {
      const relinkBtn = e.target?.closest?.("[data-project-relink]");
      if (!relinkBtn) return;
      const idx = Number(relinkBtn.getAttribute("data-project-relink"));
      if (!Number.isFinite(idx) || idx < 0 || idx >= relinkItems.length) return;
      pendingRelinkRef = relinkItems[idx];
      settingsModalEl.querySelector("#settings-project-relink-file")?.click();
    });
    settingsModalEl.querySelector("#settings-project-relink-file")?.addEventListener("change", async (e) => {
      const file = e.target?.files?.[0];
      if (!file || !pendingRelinkRef) {
        e.target.value = "";
        return;
      }
      const ref = pendingRelinkRef;
      pendingRelinkRef = null;
      const out = await runtimeRef?.tools?.resolveProjectReference?.(ref, file);
      if (!out?.ok) {
        toast(out?.message || "Relink failed");
        e.target.value = "";
        return;
      }
      if (lastRestoreReport) {
        lastRestoreReport.unresolved = (lastRestoreReport.unresolved || []).filter((u) => !(u.toolId === ref.toolId && u.referenceId === ref.referenceId));
        lastRestoreReport.restored = Number(lastRestoreReport.restored || 0) + Number(out.restored || 1);
      }
      renderRestoreReport(lastRestoreReport);
      events.emit("project:restore:report", lastRestoreReport);
      toast(out?.message || "Source relinked");
      e.target.value = "";
    });
    settingsModalEl.querySelector("#settings-project-batch-relink")?.addEventListener("click", () => {
      settingsModalEl.querySelector("#settings-project-relink-folder")?.click();
    });
    settingsModalEl.querySelector("#settings-project-relink-folder")?.addEventListener("change", async (e) => {
      const files = [...(e.target?.files || [])];
      if (!files.length) {
        e.target.value = "";
        return;
      }
      const byName = new Map(files.map((f) => [String(f.name || "").toLowerCase(), f]));
      let resolved = 0;
      for (const ref of relinkItems.slice()) {
        const expected = String(ref.expectedFileName || "").toLowerCase();
        if (!expected || !byName.has(expected)) continue;
        const file = byName.get(expected);
        const out = await runtimeRef?.tools?.resolveProjectReference?.(ref, file);
        if (out?.ok) {
          resolved += Number(out?.restored || 1);
          if (lastRestoreReport) {
            lastRestoreReport.unresolved = (lastRestoreReport.unresolved || []).filter((u) => !(u.toolId === ref.toolId && u.referenceId === ref.referenceId));
            lastRestoreReport.restored = Number(lastRestoreReport.restored || 0) + Number(out.restored || 1);
          }
        }
      }
      renderRestoreReport(lastRestoreReport);
      events.emit("project:restore:report", lastRestoreReport);
      toast(resolved ? `Batch relink restored ${resolved} source(s)` : "No filename matches found in folder");
      e.target.value = "";
    });
    const policySel = settingsModalEl.querySelector("#settings-project-data-policy");
    if (policySel) {
      policySel.value = localStorage.getItem(PROJECT_DATA_POLICY_KEY) || "reference";
      policySel.addEventListener("change", () => {
        localStorage.setItem(PROJECT_DATA_POLICY_KEY, policySel.value || "reference");
        updateProjectEstimate();
      });
    }
    const embedMaxEl = settingsModalEl.querySelector("#settings-project-embed-max");
    if (embedMaxEl) {
      embedMaxEl.value = localStorage.getItem(PROJECT_EMBED_MAX_SOUNDINGS_KEY) || "0";
      embedMaxEl.addEventListener("change", () => {
        localStorage.setItem(PROJECT_EMBED_MAX_SOUNDINGS_KEY, String(Math.max(0, Number(embedMaxEl.value || 0) || 0)));
        updateProjectEstimate();
      });
    }
    const autosaveEnabledEl = settingsModalEl.querySelector("#settings-project-autosave-enabled");
    const autosaveIntervalEl = settingsModalEl.querySelector("#settings-project-autosave-interval");
    const autosaveStampEl = settingsModalEl.querySelector("#settings-project-autosave-stamp");
    if (autosaveEnabledEl && autosaveIntervalEl) {
      autosaveEnabledEl.checked = localStorage.getItem(PROJECT_AUTOSAVE_ENABLED_KEY) === "1";
      autosaveIntervalEl.value = localStorage.getItem(PROJECT_AUTOSAVE_INTERVAL_KEY) || "90";
      autosaveStampEl.textContent = localStorage.getItem(PROJECT_AUTOSAVE_AT_KEY) || "never";
      autosaveEnabledEl.addEventListener("change", () => {
        localStorage.setItem(PROJECT_AUTOSAVE_ENABLED_KEY, autosaveEnabledEl.checked ? "1" : "0");
        configureAutosave();
      });
      autosaveIntervalEl.addEventListener("change", () => {
        localStorage.setItem(PROJECT_AUTOSAVE_INTERVAL_KEY, String(Math.max(15, Number(autosaveIntervalEl.value || 90) || 90)));
        configureAutosave();
      });
    }
    settingsModalEl.querySelector("#settings-project-restore-autosave")?.addEventListener("click", restoreAutosaveSnapshot);
    settingsModalEl.querySelector("#settings-project-clear-autosave")?.addEventListener("click", clearAutosaveSnapshot);

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeSettingsModal();
    });

    applyUiTheme(localStorage.getItem(UI_THEME_KEY) || "deep-blue");
    renderRestoreReport(lastRestoreReport);
    updateProjectEstimate();
  }

  function ensureSettingsButton() {
    if (topbarEl) {
      const existing = topbarEl.querySelector("#global-settings-btn");
      if (!existing) {
        const btn = document.createElement("button");
        btn.id = "global-settings-btn";
        btn.className = "btn";
        btn.type = "button";
        btn.textContent = "Settings";
        btn.addEventListener("click", openSettingsModal);
        topbarEl.prepend(btn);
      }
      return;
    }
    const header = rootEl.querySelector(".app-header");
    const brand = rootEl.querySelector(".brand");
    if (!header || !brand) return;
    let wrap = rootEl.querySelector(".brand-wrap");
    if (!wrap) {
      wrap = document.createElement("div");
      wrap.className = "brand-wrap";
      brand.parentElement?.insertBefore?.(wrap, brand);
      wrap.appendChild(brand);
    }
    if (wrap.querySelector("#global-settings-btn")) return;
    const btn = document.createElement("button");
    btn.id = "global-settings-btn";
    btn.className = "btn";
    btn.type = "button";
    btn.textContent = "Settings";
    btn.addEventListener("click", openSettingsModal);
    wrap.appendChild(btn);
  }

  function ensureSimulatorFullscreenHandoff() {
    const simLink = rootEl?.querySelector?.('.header-link[href="/simulator.html"]');
    if (!simLink || simLink.dataset.fullscreenHandoffBound === "1") return;
    simLink.dataset.fullscreenHandoffBound = "1";
    simLink.addEventListener("click", () => {
      if (!isFullscreenActive()) {
        try { sessionStorage.removeItem(FULLSCREEN_SIM_HANDOFF_KEY); } catch {}
        return;
      }
      try {
        sessionStorage.setItem(FULLSCREEN_SIM_HANDOFF_KEY, JSON.stringify({
          ts: Date.now(),
          from: window.location.pathname,
        }));
      } catch {}
    });
  }

  function isFullscreenActive() {
    return !!document.fullscreenElement;
  }

  function syncFullscreenUi() {
    const active = isFullscreenActive();
    if (fullscreenBtnEl) fullscreenBtnEl.textContent = active ? "Exit Fullscreen" : "Fullscreen";
    if (fullscreenExitBtnEl) {
      fullscreenExitBtnEl.setAttribute("aria-hidden", active ? "false" : "true");
      fullscreenExitBtnEl.tabIndex = active ? 0 : -1;
    }
    rootEl?.classList?.toggle("is-fullscreen", active);
  }

  async function toggleFullscreen() {
    try {
      if (!document.fullscreenEnabled) {
        toast("Fullscreen is not supported by this browser");
        return;
      }
      if (isFullscreenActive()) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
      syncFullscreenUi();
      dispatchMapResizePulse();
    } catch (error) {
      console.error("[UI] fullscreen toggle failed", error);
      toast("Fullscreen toggle failed");
    }
  }

  function ensureFullscreenButton() {
    const wrap = rootEl.querySelector(".brand-wrap");
    if (!wrap) return;
    if (!fullscreenBtnEl) {
      const btn = document.createElement("button");
      btn.id = "global-fullscreen-btn";
      btn.className = "btn";
      btn.type = "button";
      btn.textContent = "Fullscreen";
      btn.addEventListener("click", toggleFullscreen);
      wrap.appendChild(btn);
      fullscreenBtnEl = btn;
    }
    if (!fullscreenExitBtnEl) {
      const btn = document.createElement("button");
      btn.id = "global-fullscreen-exit-btn";
      btn.className = "fullscreen-exit-btn";
      btn.type = "button";
      btn.textContent = "Exit";
      btn.title = "Exit Fullscreen";
      btn.setAttribute("aria-label", "Exit Fullscreen");
      btn.setAttribute("aria-hidden", "true");
      btn.tabIndex = -1;
      btn.addEventListener("click", toggleFullscreen);
      rootEl.appendChild(btn);
      fullscreenExitBtnEl = btn;
    }
    if (!fullscreenBound) {
      document.addEventListener("fullscreenchange", syncFullscreenUi);
      fullscreenBound = true;
    }
    syncFullscreenUi();
  }

  function ensureFileDropOverlay() {
    if (fileDropOverlayEl) return fileDropOverlayEl;
    const el = document.createElement("div");
    el.className = "file-drop-overlay";
    el.setAttribute("aria-hidden", "true");
    el.innerHTML = `
      <div class="file-drop-overlay-card">
        <div class="file-drop-overlay-title">Drop files to import</div>
        <div class="file-drop-overlay-copy">KMZ, KML, GeoJSON, GPX, DXF, SHP ZIP</div>
      </div>
    `;
    rootEl.appendChild(el);
    fileDropOverlayEl = el;
    return el;
  }

  function setFileDropOverlayVisible(visible) {
    const el = ensureFileDropOverlay();
    el.setAttribute("aria-hidden", visible ? "false" : "true");
  }

  function isImportableDraggedFile(event) {
    const items = Array.from(event?.dataTransfer?.items || []);
    if (!items.length) return false;
    return items.some((item) => item.kind === "file");
  }

  function ensureGlobalFileDrop() {
    ensureFileDropOverlay();
    const reset = () => {
      fileDropDepth = 0;
      setFileDropOverlayVisible(false);
    };
    rootEl.addEventListener("dragenter", (e) => {
      if (!isImportableDraggedFile(e)) return;
      fileDropDepth += 1;
      setFileDropOverlayVisible(true);
    });
    rootEl.addEventListener("dragover", (e) => {
      if (!isImportableDraggedFile(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      setFileDropOverlayVisible(true);
    });
    rootEl.addEventListener("dragleave", (e) => {
      if (!isImportableDraggedFile(e)) return;
      fileDropDepth = Math.max(0, fileDropDepth - 1);
      if (fileDropDepth === 0 || e.target === rootEl) setFileDropOverlayVisible(false);
    });
    rootEl.addEventListener("drop", async (e) => {
      if (!isImportableDraggedFile(e)) return;
      e.preventDefault();
      reset();
      const files = Array.from(e.dataTransfer?.files || []);
      if (!files.length) return;
      try {
        const out = await runtimeRef?.tools?.importDroppedFiles?.(files);
        if (out?.handled) {
          toast(`Imported ${out.imported} dropped file${out.imported === 1 ? "" : "s"} into Linework`);
          return;
        }
        if (out?.pendingSiblingLookup) return;
      } catch (err) {
        console.error("[UI] dropped file import failed", err);
        toast(err?.message || "Dropped file import failed");
        return;
      }
      toast("Dropped files were not recognized for import");
    });
    window.addEventListener("dragend", reset);
    window.addEventListener("drop", (e) => {
      if (e.target === rootEl || rootEl.contains(e.target)) return;
      reset();
    });
  }

  function bindRuntime(runtime) {
    runtimeRef = runtime || null;
    configureAutosave();
  }

  function addTopbarButton({ id, label, onClick, primary = false }) {
    const btn = document.createElement("button");
    btn.className = `btn ${primary ? "primary" : ""}`;
    btn.id = id;
    btn.textContent = label;
    btn.addEventListener("click", onClick);
    topbarEl.appendChild(btn);
    return () => btn.remove();
  }

  function mountLeftPanel(html) {
    leftPanelEl.innerHTML = `<div class="panel-content">${html}</div>`;
    addResizer(leftPanelEl, "left", "--leftPanelW", LEFT_W_KEY);
  }

  function mountRightPanel(html) {
    rightPanelEl.innerHTML = `<div class="panel-content">${html}</div>`;
    addResizer(rightPanelEl, "right", "--rightPanelW", RIGHT_W_KEY);
  }

  function mountBottomPanel(html, options = {}) {
    const ownerId = String(options?.ownerId || "default");
    const title = String(options?.title || "Bottom Panel");
    const activate = options?.activate === true;
    const existing = bottomPanelEntries.get(ownerId);
    const bodyEl = existing?.bodyEl || document.createElement("div");
    bodyEl.className = "bottom-panel-view";
    bodyEl.innerHTML = String(html || "");
    bottomPanelEntries.set(ownerId, {
      id: ownerId,
      title,
      bodyEl,
      onShow: typeof options?.onShow === "function" ? options.onShow : existing?.onShow || null,
    });
    if (!activeBottomPanelId || !bottomPanelEntries.has(activeBottomPanelId) || activate) {
      activeBottomPanelId = ownerId;
    }
    renderBottomPanelHost();
  }

  function unmountBottomPanel(ownerId = "default") {
    const id = String(ownerId || "default");
    if (!bottomPanelEntries.has(id)) return;
    bottomPanelEntries.delete(id);
    if (activeBottomPanelId === id) {
      activeBottomPanelId = [...bottomPanelEntries.keys()].pop() || "";
    }
    renderBottomPanelHost();
  }

  function showBottomPanel(ownerId) {
    const id = String(ownerId || "");
    if (!id || !bottomPanelEntries.has(id)) return false;
    activeBottomPanelId = id;
    renderBottomPanelHost();
    return true;
  }

  function toast(message) {
    const el = document.createElement("div");
    el.className = "card";
    el.style.pointerEvents = "auto";
    el.textContent = message;
    hudEl.appendChild(el);
    setTimeout(() => el.remove(), 2600);
  }

  events.on("state:changed", ({ meta }) => {
    if (meta?.reason === "reset") toast("State reset");
  });

  addResizer(leftPanelEl, "left", "--leftPanelW", LEFT_W_KEY);
  addResizer(rightPanelEl, "right", "--rightPanelW", RIGHT_W_KEY);
  addResizer(bottomPanelEl, "top", "--bottomPanelH", BOTTOM_H_KEY);
  ensureThemeToggle();
  ensureCaptureButton();
  ensureDockToggles();
  ensureSettingsModal();
  applyUiTheme(localStorage.getItem(UI_THEME_KEY) || "deep-blue");
  ensureSettingsButton();
  ensureSimulatorFullscreenHandoff();
  ensureFullscreenButton();
  ensureGlobalFileDrop();
  configureAutosave();

  return {
    bindRuntime,
    addTopbarButton,
    mountLeftPanel,
    mountRightPanel,
    mountBottomPanel,
    unmountBottomPanel,
    showBottomPanel,
    toast,
    setDockOpen,
  };
}
